import {ArticleCard} from '@/components/articles/ArticlesGrid'
import {t} from '@/lib/i18n'
import {urlForImage} from '@/sanity/image'
import type {CampaignDocument} from '@/types/sanity'

type CampaignsGridProps = {
  campaigns: CampaignDocument[]
  locale?: string
}

type CampaignTimingState = 'active' | 'upcoming' | 'expired' | 'unscheduled'

const campaignIdentity = (campaign: CampaignDocument) => {
  const normalizedId = campaign._id.replace(/^drafts\./, '')
  const slug = campaign.slug?.current || ''
  return slug ? `${normalizedId}::${slug}` : normalizedId
}

const hrefFromSlug = (slug?: string) => {
  if (!slug) return '#'
  return slug.startsWith('/') ? slug : `/${slug}`
}

const teaserImageUrl = (campaign: CampaignDocument) => {
  const image = campaign.teaserImage?.image || campaign.bannerImage?.image
  if (!image) return undefined
  return urlForImage(image).width(960).height(720).fit('crop').url()
}

export const campaignTimingState = (campaign: CampaignDocument): CampaignTimingState => {
  const now = Date.now()
  const startAt = campaign.duration?.startAt ? new Date(campaign.duration.startAt).getTime() : null
  const endAt = campaign.duration?.endAt ? new Date(campaign.duration.endAt).getTime() : null

  if (!startAt && !endAt) return 'unscheduled'
  if (startAt && now < startAt) return 'upcoming'
  if (endAt && now > endAt) return 'expired'
  return 'active'
}

export const uniqueCampaigns = (campaigns: CampaignDocument[]) => {
  const seen = new Set<string>()

  return campaigns.filter((campaign) => {
    const identity = campaignIdentity(campaign)
    if (seen.has(identity)) {
      return false
    }
    seen.add(identity)
    return true
  })
}

function CampaignMeta({campaign}: {campaign: CampaignDocument}) {
  if (!campaign.tag) return null

  return (
    <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
      {campaign.tag}
    </span>
  )
}

export function CampaignCard({
  campaign,
  cardClassName,
  locale,
}: {
  campaign: CampaignDocument
  cardClassName?: string
  locale?: string
}) {
  return (
    <ArticleCard
      layout="horizontal"
      href={hrefFromSlug(campaign.slug?.current)}
      imageUrl={teaserImageUrl(campaign)}
      imageAlt={campaign.teaserImage?.alt || campaign.bannerImage?.alt || campaign.title || ''}
      meta={<CampaignMeta campaign={campaign} />}
      title={campaign.title || t(locale, 'campaign.untitled', 'Untitled campaign')}
      subtitle={campaign.subtitle}
      excerpt={campaign.description}
      ctaLabel={campaign.ctaText || t(locale, 'common.readMore', 'Read more')}
      ctaVariant="button"
      className={cardClassName}
      locale={locale}
    />
  )
}

export function CampaignsGrid({campaigns, locale}: CampaignsGridProps) {
  const dedupedCampaigns = uniqueCampaigns(campaigns)

  if (!dedupedCampaigns.length) {
    return (
      <p className="text-muted-foreground">
        {t(locale, 'campaign.noCampaignsMessage', 'No campaigns found.')}
      </p>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-card-gap">
      {dedupedCampaigns.map((campaign) => (
        <CampaignCard key={campaignIdentity(campaign)} campaign={campaign} locale={locale} />
      ))}
    </div>
  )
}
